import { useApplicationModals } from "app/common/hooks";
import { Badge } from "app/components/atoms/badge";
import { Button, IconButton } from "app/components/atoms/button";
import Typography from "app/components/atoms/typography";
import { ModalMolecule } from "app/components/molecules/modal";
import api from "app/integration/api";
import { useReactToPrint } from "react-to-print";
import { useState, useEffect, useRef } from "react";
import Table from "app/components/atoms/table";
import { MinusCircleIcon, PlusCircleIcon } from "@heroicons/react/24/outline";

function ViewReturHistoryModal({ onSuccess = () => { } }) {
  const { showStates, getPayloads, closeModal } = useApplicationModals();
  const payload = getPayloads("returHistory.view");
  const transactionId = payload?.transactionId;

  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});
  const [order, setOrder] = useState(null);

  const componentRef = useRef();
  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  });

  useEffect(() => {
    const fetchReturHistory = async () => {
      if (!transactionId) return;

      setLoading(true);
      setError(null);

      try {
        const response = await api.returCustomer.index({
          transaction_id: transactionId,
          sort: "DESC",
          order: "createdAt"
        });

        if (response.data) {
          setHistory(response.data);
        }
      } catch (err) {
        console.error('Error fetching return history:', err);
        setError('Gagal memuat riwayat retur');
      } finally {
        setLoading(false);
      }
    };

    fetchReturHistory();

    const fetchOrder = async () => {
      if (!transactionId) return;

      setLoading(true);
      setError(null);

      try {
        const response = await api.salesOrder.show(transactionId);

        if (response.data) {
          setOrder(response.data);
        }
      } catch (err) {
        console.error('Error fetching order:', err);
        setError('Gagal memuat detail order');
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [transactionId]);

  const handleClose = () => closeModal("returHistory.view");

  const getStatusBadge = (status) => {
    const statusMap = {
      pending: { color: 'yellow', label: 'Menunggu' },
      approved: { color: 'green', label: 'Disetujui' },
      rejected: { color: 'red', label: 'Ditolak' },
      processed: { color: 'blue', label: 'Diproses' },
      completed: { color: 'green', label: 'Selesai' }
    };

    const statusInfo = statusMap[status] || { color: 'gray', label: status };
    return <Badge color={statusInfo.color}>{statusInfo.label}</Badge>;
  };

  if (!showStates["returHistory.view"]) return null;

  return (
    <ModalMolecule
      show={showStates["returHistory.view"]}
      title={`Riwayat Retur #${order?.order_code || ''}`}
      onClose={handleClose}
      size="2xl"
    >
      <div ref={componentRef} className="space-y-4">
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          </div>
        ) : error ? (
          <div className="text-red-500 text-center py-4">{error}</div>
        ) : history.length === 0 ? (
          <div className="text-gray-500 text-center py-4">Tidak ada riwayat retur</div>
        ) : (
          <Table.Container>
            <Table.Wrapper>
              <Table.Header>
                <Table.HeaderRow>
                  <Table.Heading></Table.Heading>
                  <Table.Heading>Produk</Table.Heading>
                  <Table.Heading>Jumlah</Table.Heading>
                  <Table.Heading>Alasan</Table.Heading>
                  <Table.Heading>Status</Table.Heading>
                  <Table.Heading>Tanggal</Table.Heading>
                </Table.HeaderRow>
              </Table.Header>
              <Table.Body>
                {history.map((retur) => (
                  <>
                    <Table.BodyRow key={retur.id}>
                    <Table.Cell>
                      {retur?.product_code_list?.length > 0 && (
                        <IconButton testId="modal-view-retur-history-minus-circle-button"
                        icon={expandedRows[retur.id] ? MinusCircleIcon : PlusCircleIcon}
                        onClick={(e) => {
                          setExpandedRows(prev => ({
                            ...prev,
                            [retur.id]: !prev[retur.id]
                          }));
                        }}
                      />
                      )}
                    </Table.Cell>
                      <Table.Cell>{retur.product_name}</Table.Cell>
                      <Table.Cell>{retur.qty}</Table.Cell>
                      <Table.Cell>{retur.reason}</Table.Cell>
                      <Table.Cell>{getStatusBadge(retur.status)}</Table.Cell>
                      <Table.Cell>
                        <Typography.Date date={retur.createdAt} format="dd/MM/yyyy HH:mm" />
                      </Table.Cell>
                    </Table.BodyRow>
                    {
                      expandedRows[retur.id] && retur?.product_code_list?.length > 0 && (
                        <Table.BodyRow className="bg-gray-50">
                          <Table.Cell colSpan="9">
                            <div className="px-4 py-3">
                              <div className="flex items-center mb-3">
                                <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">Daftar Kode Produk</h4>
                                <span className="ml-2 px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-600 rounded-full">
                                  {retur.product_code_list.length} item
                                </span>
                              </div>
                              <div className="space-y-2">
                                {retur.product_code_list.map((code, idx) => (
                                  <div key={idx} className="group bg-white rounded-lg border border-gray-100 hover:border-blue-100 transition-colors duration-150 shadow-sm hover:shadow">
                                    <div className="p-3">
                                      <div className="flex items-start justify-between">
                                        <div className="flex-1 min-w-0">
                                          <div className="flex items-center space-x-2">
                                            <div className="bg-gray-50 px-3 py-1.5 rounded-md border border-gray-100">
                                              <span className="font-mono text-sm font-medium text-gray-800">{code.qr_code}</span>
                                            </div>
                                            <div className="flex-shrink-0">
                                              <Badge
                                                color={
                                                  code.reason ? 'red' :
                                                    'gray'
                                                }
                                                className="px-2 py-1 text-xs font-medium"
                                              >
                                                {code.retur_reason || 'Tidak ada alasan'}
                                              </Badge>
                                            </div>
                                          </div>
                                          {code.description && (
                                            <p className="mt-2 text-sm text-gray-600">
                                              <span className="font-medium">Catatan: </span>
                                              {code.description}
                                            </p>
                                          )}
                                        </div>
                                        <div className="ml-2 flex-shrink-0 flex items-center">
                                          <span className="text-xs text-gray-400">
                                            {new Date(code.createdAt || new Date()).toLocaleDateString('id-ID', {
                                              day: '2-digit',
                                              month: 'short',
                                              year: 'numeric',
                                              hour: '2-digit',
                                              minute: '2-digit'
                                            })}
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </Table.Cell>
                        </Table.BodyRow>
                      )
                    }
                  </>
                ))}
              </Table.Body>
            </Table.Wrapper>
          </Table.Container>
        )}
      </div>
    </ModalMolecule>
  );
}

export default ViewReturHistoryModal;
